import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminContactSettingsPage = () => {
  const { getAuthHeaders } = useApp();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/contact`);
      setSettings(data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/settings/contact`, settings, {
        headers: getAuthHeaders(),
      });
      toast.success('Contact settings updated!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP images.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      // Store relative URL path (just /api/images/{id}) to avoid domain issues
      const imageUrl = `/api/images/${response.data.id}`;
      setSettings({ ...settings, contact_image_url: imageUrl });
      toast.success('Image uploaded! Click Save to apply.');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const removeImage = () => {
    setSettings({ ...settings, contact_image_url: '' });
  };

  // Helper to get full image URL from relative path
  const getImageUrl = (url) => {
    if (!url) return '';
    // If it's already a full URL, return as is (for backwards compatibility)
    if (url.startsWith('http')) return url;
    // If it's a relative path, prepend BACKEND_URL
    return `${BACKEND_URL}${url}`;
  };

  if (!settings) return <AdminLayout><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-contact-settings-page">
        <h1 className="text-4xl font-serif mb-8">Contact Settings</h1>

        <form onSubmit={handleSubmit} className="glass p-8 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Email</label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Phone</label>
              <input
                type="tel"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Address</label>
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Romanian"
                value={settings.address?.ro || ''}
                onChange={(e) => setSettings({ ...settings, address: { ...settings.address, ro: e.target.value } })}
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="English"
                value={settings.address?.en || ''}
                onChange={(e) => setSettings({ ...settings, address: { ...settings.address, en: e.target.value } })}
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="French"
                value={settings.address?.fr || ''}
                onChange={(e) => setSettings({ ...settings, address: { ...settings.address, fr: e.target.value } })}
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Website</label>
              <input
                type="url"
                value={settings.website}
                onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Facebook</label>
              <input
                type="url"
                value={settings.facebook}
                onChange={(e) => setSettings({ ...settings, facebook: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Instagram</label>
            <input
              type="url"
              value={settings.instagram}
              onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
            />
          </div>

          {/* Contact Image (Support/Team Photo) */}
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Contact Page Image</label>
            <p className="text-xs text-muted-foreground mb-3">This image appears at the top of the contact section (e.g., customer support photo)</p>
            
            {/* Upload from file */}
            <div className="mb-4 p-4 bg-slate-900/50 border border-white/10 rounded">
              <label className="text-xs text-muted-foreground mb-2 block">Upload Image File</label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="flex-1 text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                />
                {uploadingImage && <span className="text-xs text-muted-foreground">Uploading...</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Max 5MB • JPG, PNG, GIF, WebP</p>
            </div>

            {/* Or add by URL */}
            <div className="mb-4">
              <label className="text-xs text-muted-foreground mb-2 block">Image URL (auto-filled after upload)</label>
              <input
                type="text"
                placeholder="/api/images/... or https://..."
                value={settings.contact_image_url || ''}
                onChange={(e) => setSettings({ ...settings, contact_image_url: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
            </div>

            {/* Image preview */}
            {settings.contact_image_url && (
              <div className="relative inline-block">
                <img src={getImageUrl(settings.contact_image_url)} alt="Contact" className="w-full h-48 object-cover rounded" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 p-1 rounded hover:bg-red-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Map Embed URL */}
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">OpenStreetMap Embed URL</label>
            <p className="text-xs text-muted-foreground mb-3">
              Free map - no API needed! Go to <a href="https://www.openstreetmap.org" target="_blank" rel="noopener noreferrer" className="text-primary underline">openstreetmap.org</a> → Search location → Click Share → Select HTML → Copy the src URL
            </p>
            <input
              type="url"
              placeholder="https://www.openstreetmap.org/export/embed.html?bbox=..."
              value={settings.map_embed_url || ''}
              onChange={(e) => setSettings({ ...settings, map_embed_url: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:outline-none focus:border-primary"
            />
            {settings.map_embed_url && (
              <div className="mt-4">
                <iframe
                  src={settings.map_embed_url}
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Location Map Preview"
                  className="rounded"
                />
              </div>
            )}
          </div>

          <Button type="submit" disabled={loading} data-testid="save-contact-settings-button" className="w-full bg-primary text-primary-foreground">
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminContactSettingsPage;